import { ReactNode } from "react";

import { InfoCircle } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import SearchInput from "components/SearchInput";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import Tooltip from "ds/components/Tooltip";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import NonRootApiKeysToggle from "components/ApiKeys/NonRootApiKeysToggle";
import ApiKeyManagementFromNonHumansToggle from "components/ApiKeys/ApiKeyManagementFromNonHumansToggle";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import useCanManageApiKeys from "shared/ApiKey/access/useCanManageApiKeys";

import { SettingsContext } from "../Context";
import SeatLimitsNotification from "../SeatLimitsNotification";
import { SEAT_LIMITS_TOOLTIP } from "../SeatLimitsNotification/constants";
import { SeatLimitsNotificationEntity } from "../SeatLimitsNotification/types";
import useSeatLimit from "../SeatLimitsNotification/useSeats";
import { UserManagementActivationStatus } from "../types";
import UserManagementWarningCallout from "../UserManagementWarningCallout";
import { API_KEYS_FILTERS_ORDER_SETTINGS_KEY } from "./List/constants";

type ApiKeysPageLayoutProps = {
  children: ReactNode;
  openCreateApiKeyDrawer?: () => void;
  openInfoDrawer: () => void;
  hasApiKeys?: boolean;
};

const ApiKeysPageLayout = ({
  children,
  openCreateApiKeyDrawer,
  openInfoDrawer,
  hasApiKeys = false,
}: ApiKeysPageLayoutProps) => {
  const { enableApiKeyCreationForNonRootAdminsFrontend } = useTypedFlags();
  const { seats, activationStatus } = useTypedContext(SettingsContext);
  const { isLimitReached } = useSeatLimit(seats.apiKey);
  const canManageApiKeys = useCanManageApiKeys();

  return (
    <>
      {activationStatus === UserManagementActivationStatus.ACTIVE && (
        <SeatLimitsNotification
          entity={SeatLimitsNotificationEntity.ApiKey}
          seats={seats.apiKey}
          type="callout"
        />
      )}
      <UserManagementWarningCallout analyticsPage={AnalyticsPageOrganization.OrganizationApiKeys} />
      <PageInfoHeaderWrapper>
        <Box direction="column" fullWidth>
          <Box fullWidth justify="between">
            <Box direction="row" align="center" __deprecatedGap="1.1rem">
              <PageInfoHeaderTitle title="API keys" />
              <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
                Open info drawer
              </ButtonIcon>
            </Box>
            <PageInfoHeaderActions>
              {hasApiKeys && (
                <SearchInput
                  placeholder="Search..."
                  filtersOrderSettingsKey={API_KEYS_FILTERS_ORDER_SETTINGS_KEY}
                />
              )}
              {canManageApiKeys && openCreateApiKeyDrawer && (
                <Tooltip
                  active={isLimitReached}
                  on={(props) => (
                    <Button
                      onPress={openCreateApiKeyDrawer}
                      variant="primary"
                      disabled={isLimitReached}
                      {...props}
                    >
                      Create API key
                    </Button>
                  )}
                >
                  {SEAT_LIMITS_TOOLTIP}
                </Tooltip>
              )}
            </PageInfoHeaderActions>
          </Box>
          {canManageApiKeys && (
            <Box margin="large 0 small" direction="row">
              {enableApiKeyCreationForNonRootAdminsFrontend && <NonRootApiKeysToggle />}
              <ApiKeyManagementFromNonHumansToggle />
            </Box>
          )}
        </Box>
      </PageInfoHeaderWrapper>
      <ListContentWrapper>{children}</ListContentWrapper>
    </>
  );
};

export default ApiKeysPageLayout;
